<template>
  <!-- POV crosshair, or the scope ring while the watched player's weapon is zoomed. -->
  <template v-if="camera === 'pov' && target">
    <div
      v-if="target.zoomLevel > 0"
      class="absolute inset-0 pointer-events-none z-20"
      style="background: radial-gradient(circle, transparent 0, transparent 38vmin, black 38.5vmin)"
    />
    <div v-else class="absolute top-1/2 left-1/2 w-4 h-4 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-20">
      <div class="absolute top-1/2 left-0 w-full h-0.5 -translate-y-1/2 bg-green-400" />
      <div class="absolute left-1/2 top-0 h-full w-0.5 -translate-x-1/2 bg-green-400" />
    </div>
  </template>

  <div
    v-if="overlay"
    class="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex gap-3 items-end text-sm"
  >
    <div class="flex flex-col gap-2 px-3 py-2 bg-gray-800 rounded-md">
      <p class="text-xs uppercase text-text-secondary" :title="$t('viewer.input_overlay.reconstructed_info')">
        <span>{{ $t('viewer.input_overlay.reconstructed') }}</span> <span class="cursor-help">ⓘ</span>
      </p>
      <div v-if="hasInput" class="grid grid-cols-3 gap-1 text-center">
        <span
          v-for="key in ['W', 'A', 'S', 'D']"
          :key="key"
          class="w-7 py-1 rounded"
          :class="[key === 'W' ? 'col-start-2' : '', overlay.keys[key] ? lit : unlit]"
        >{{ key }}</span>
      </div>
      <p v-else class="text-text-secondary">{{ $t('viewer.input_overlay.no_movement_input') }}</p>
      <div class="flex gap-1">
        <span
          v-for="indicator in overlay.indicators"
          :key="indicator.label"
          class="px-2 py-1 rounded"
          :class="indicator.on ? lit : unlit"
        >{{ indicator.label }}</span>
      </div>
    </div>
    <div class="flex flex-col px-3 py-2 bg-gray-800 rounded-md text-text-primary">
      <p><span class="text-2xl font-bold">{{ overlay.velocity.speed }}</span> <span>u/s</span></p>
      <p v-if="overlay.velocity.vertical !== null" class="text-text-secondary">
        <span>{{ $t('viewer.input_overlay.vertical') }}</span> <span>{{ overlay.velocity.vertical }}</span>
      </p>
      <svg class="w-32 h-8" viewBox="0 0 64 32" preserveAspectRatio="none">
        <polyline :points="overlay.sparkline" fill="none" stroke="currentColor" stroke-width="1" />
      </svg>
    </div>
  </div>
</template>

<script setup>
// wi_6b5960f9-952c-48fc-ab25-68e9f9e5b2da: crosshair, scope, reconstructed inputs and velocity
// over the 3D view. Imported by the page, not by the async Viewer3dCanvas chunk.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { framePlayers, duckAmount } from '../../utils/demoViewer/scene3d.js';
import { alivePlayers } from '../../utils/demoViewer/selection.js';
import {
  movementKeys, velocityReadout, hasMovementInput, isFiring,
} from '../../utils/demoViewer/inputOverlay.js';

const props = defineProps({
  roundData: Object,
  currentFrame: Number,
  camera: String,
  selectedSteamId: String,
  weaponStates: Object,
});

const { t } = useI18n();
const lit = 'bg-gray-200 text-gray-900';
const unlit = 'bg-gray-700 text-text-secondary';
const SPARKLINE_FRAMES = 64;

const frameIndex = computed(() => {
  const count = props.roundData?.frames?.length ?? 0;
  return Math.max(0, Math.min(props.currentFrame || 0, count - 1));
});
const frame = computed(() => props.roundData?.frames?.[frameIndex.value]);
const hasInput = computed(() => hasMovementInput(props.roundData?.frames));
const weaponOf = (player) => props.weaponStates.stateAt(player.activeWeaponID, frame.value.tick);

// The player the cameras watch: the selection, else the first alive CT.
const target = computed(() => {
  const steamID = props.selectedSteamId ?? alivePlayers(frame.value)[0]?.steamID;
  const player = framePlayers(frame.value).find((p) => p.steamID === steamID);
  return player ? { zoomLevel: weaponOf(player).zoomLevel } : null;
});

// Overlay and velocity for the selected player, at the exact current frame.
const overlay = computed(() => {
  const player = framePlayers(frame.value).find((p) => p.steamID === props.selectedSteamId);
  if (!player) return null;
  const { tick } = frame.value;
  const weapon = weaponOf(player);
  const speeds = props.roundData.frames
    .slice(Math.max(0, frameIndex.value - SPARKLINE_FRAMES + 1), frameIndex.value + 1)
    .map((f) => framePlayers(f).find((p) => p.steamID === player.steamID))
    .map((p) => (p ? velocityReadout(p).speed : 0));
  const top = Math.max(250, ...speeds);
  return {
    keys: movementKeys(player.toggleButtonMask),
    indicators: [
      { label: t('viewer.input_overlay.crouch'), on: duckAmount(player) >= 0.5 },
      { label: t('viewer.input_overlay.air'), on: Boolean(player.isAirborne) },
      { label: t('viewer.input_overlay.fire'), on: isFiring(props.roundData.weaponFires, player.steamID, tick) },
      { label: t('viewer.input_overlay.reload'), on: Boolean(weapon.isReloading) },
      { label: t('viewer.input_overlay.scope'), on: weapon.zoomLevel > 0 },
    ],
    velocity: velocityReadout(player),
    sparkline: speeds.map((speed, i) => `${i},${32 - (speed / top) * 32}`).join(' '),
  };
});
</script>
